import React, {useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    OutlinedInput
} from '@mui/material';
import axios from 'axios';
import CronConfigDialog from './CronConfigDialog';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import FieldHelpSuffix from '../common/FieldHelpSuffix';

interface CreateUpdateSchedulerDialogProps {
    open: boolean;
    onClose: () => void;
    onJobCreated: () => void;
}

enum JobSource {
    CONFLUENCE = "CONFLUENCE",
    GOOGLE_DRIVE = "GOOGLE_DRIVE"
}

const CreateUpdateSchedulerDialog: React.FC<CreateUpdateSchedulerDialogProps> = ({open, onClose, onJobCreated}) => {
    const {t} = useTranslation();
    const [source, setSource] = useState<JobSource>(JobSource.CONFLUENCE);
    const [cronExpression, setCronExpression] = useState('');
    const [catalogName, setCatalogName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCronConfigDialogOpen, setIsCronConfigDialogOpen] = useState(false);
    const {showSnackbar} = useSnackbar();

    const handleSubmit = async () => {
        if (!cronExpression || !catalogName) {
            setError(t('createUpdateScheduler.allFieldsRequired') as string);
            return;
        }
        setError('');
        setLoading(true);
        try {
            await axios.post('/jobs', {
                cronExpression,
                catalogName
            }, {
                params: {
                    source: source
                }
            });
            showSnackbar(t('createUpdateScheduler.updateAddedSuccess'), 'success');
            onJobCreated();
            onClose();
        } catch (err) {
            setError(t('createUpdateScheduler.createError') as string);
            showSnackbar(parseError(error), "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setCronExpression('');
        setCatalogName('');
        setError('');
        onClose();
    }

    const handleOpenCronDialog = () => {
        setIsCronConfigDialogOpen(true);
    };

    const handleCloseCronDialog = () => {
        setIsCronConfigDialogOpen(false);
    };

    const handleSaveCron = (newCron: string) => {
        setCronExpression(newCron);
    };

    return (
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle sx={{padding: '24px 32px 16px'}}>{t('createUpdateScheduler.title')}</DialogTitle>
            <DialogContent sx={{padding: '16px 32px'}}>
                {error && <Typography color="error" gutterBottom>{error}</Typography>}
                <FormControl fullWidth margin="normal">
                    <InputLabel id="source-select-label">{t('createUpdateScheduler.source')}</InputLabel>
                    <Select
                        labelId="source-select-label"
                        value={source}
                        label={t('createUpdateScheduler.source')}
                        onChange={(e) => setSource(e.target.value as JobSource)}
                        sx={{
                            '& .MuiSelect-icon': {
                                right: 40,
                            },
                        }}
                        input={
                            <OutlinedInput
                                label={t('createUpdateScheduler.source')}
                                endAdornment={
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('createUpdateScheduler.help.source')}
                                        ariaLabel={t('createUpdateScheduler.help.source') as string}
                                    />
                                }
                                sx={{ pr: '72px' }}
                            />
                        }
                    >
                        <MenuItem value={JobSource.CONFLUENCE}>Confluence</MenuItem>
                        <MenuItem value={JobSource.GOOGLE_DRIVE}>Google Drive</MenuItem>
                    </Select>
                </FormControl>
                <Grid container spacing={2} alignItems="center" sx={{mt: 1, mb: 2}}>
                    <Grid item xs>
                        <TextField
                            label={t('createUpdateScheduler.cronExpression')}
                            fullWidth
                            value={cronExpression}
                            onChange={(e) => setCronExpression(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('createUpdateScheduler.help.cronExpression')}
                                        ariaLabel={t('createUpdateScheduler.help.cronExpression') as string}
                                    />
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={handleOpenCronDialog}
                            sx={{height: '56px'}}
                        >
                            {t('createUpdateScheduler.configure')}
                        </Button>
                    </Grid>
                </Grid>
                <TextField
                    label={t('createUpdateScheduler.catalogName')}
                    fullWidth
                    margin="normal"
                    value={catalogName}
                    onChange={(e) => setCatalogName(e.target.value)}
                    required
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix
                                asAdornment
                                title={t('createUpdateScheduler.help.catalogName')}
                                ariaLabel={t('createUpdateScheduler.help.catalogName') as string}
                            />
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions sx={{padding: '16px 32px 24px', justifyContent: 'flex-end', gap: '8px'}}>
                <CancelButton onClick={handleCloseDialog} disabled={loading}>
                    {t('createUpdateScheduler.cancel')}
                </CancelButton>
                <Box sx={{position: 'relative'}}>
                    <ActionButton
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {t('createUpdateScheduler.create')}
                    </ActionButton>
                    {loading && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: 'primary.main',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Box>
            </DialogActions>
            <CronConfigDialog
                open={isCronConfigDialogOpen}
                currentCron={cronExpression || '0 0 12 * * ?'}
                onClose={handleCloseCronDialog}
                onSave={handleSaveCron}
                mode="create"
            />
        </Dialog>
    );
};

export default CreateUpdateSchedulerDialog;
